import React, { useState } from 'react';
import { Button } from './ui/button';
import { FileText, Download, X, ShieldCheck, Eye, ExternalLink } from 'lucide-react';

const STATUTE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/statute.pdf`;

const Statute = () => {
  const [open, setOpen] = useState(false);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = STATUTE_URL;
    a.download = 'estatuto_fundacion_futuros_del_deporte.pdf';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <section id="estatuto" className="py-16 bg-gradient-to-b from-white via-sky-50/40 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white border-2 border-sky-100 rounded-3xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-5 gap-0">
              {/* Left: Info */}
              <div className="md:col-span-3 p-8 sm:p-10">
                <div className="inline-flex items-center space-x-2 bg-sky-50 border border-sky-200 rounded-full px-3 py-1 mb-5">
                  <ShieldCheck className="w-4 h-4 text-sky-600" />
                  <span className="text-xs font-semibold text-sky-700 uppercase tracking-wide">
                    Transparencia Institucional
                  </span>
                </div>

                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                  Estatuto Social <br />
                  <span className="text-sky-600">Fundación Futuros del Deporte</span>
                </h2>

                <p className="text-gray-600 leading-relaxed mb-6">
                  Nuestra fundación cuenta con <strong>Personería Jurídica</strong> otorgada por el
                  Gobierno de la Provincia de La Rioja mediante Resolución M.S.J. y D.H. N° 128 del
                  29 de noviembre de 2021. Podés consultar el Estatuto Social completo con datos
                  institucionales, objeto, estructura y régimen de funcionamiento.
                </p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500 font-semibold uppercase">CUIT</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">30-71755372-8</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500 font-semibold uppercase">Personería</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">Res. N° 128/2021</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    data-testid="view-statute-btn"
                    onClick={() => setOpen(true)}
                    className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-6 text-base transition-colors"
                  >
                    <Eye className="w-5 h-5 mr-2" />
                    Ver Estatuto
                  </Button>
                  <Button
                    data-testid="download-statute-btn"
                    onClick={handleDownload}
                    variant="outline"
                    className="border-2 border-sky-300 text-sky-700 hover:bg-sky-50 px-6 py-6 text-base"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Descargar PDF
                  </Button>
                </div>
              </div>

              {/* Right: Visual card */}
              <div className="md:col-span-2 bg-gradient-to-br from-sky-600 to-blue-700 flex items-center justify-center p-8 md:p-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-4 right-4 w-32 h-32 bg-white rounded-full blur-3xl"></div>
                  <div className="absolute bottom-4 left-4 w-40 h-40 bg-sky-300 rounded-full blur-3xl"></div>
                </div>
                <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-6 w-full max-w-[220px] transform rotate-2 hover:rotate-0 transition-transform duration-300">
                  <div className="border-t-4 border-sky-600 pt-4">
                    <FileText className="w-10 h-10 text-sky-600 mb-3" />
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                      Documento Oficial
                    </p>
                    <p className="font-bold text-gray-900 text-sm mb-3 leading-tight">
                      Estatuto Social
                    </p>
                    <div className="space-y-1.5">
                      <div className="h-1.5 bg-gray-200 rounded-full w-full"></div>
                      <div className="h-1.5 bg-gray-200 rounded-full w-4/5"></div>
                      <div className="h-1.5 bg-gray-200 rounded-full w-full"></div>
                      <div className="h-1.5 bg-gray-200 rounded-full w-3/4"></div>
                      <div className="h-1.5 bg-gray-200 rounded-full w-5/6"></div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center space-x-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
                      <span className="text-[10px] text-gray-500 font-medium">Verificado</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {open && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex flex-col p-3 sm:p-6"
          data-testid="statute-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex items-center justify-between mb-3 max-w-6xl mx-auto w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center space-x-2 text-white">
              <FileText className="w-5 h-5" />
              <span className="font-semibold text-sm sm:text-base">Estatuto Social - FUNDACIÓN FUTUROS DEL DEPORTE</span>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                data-testid="modal-download-btn"
                onClick={handleDownload}
                size="sm"
                className="bg-white text-sky-900 hover:bg-sky-50"
              >
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Descargar</span>
              </Button>
              <a
                href={STATUTE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-sky-900 hover:bg-sky-50 h-9 px-3 rounded-md text-sm font-medium"
              >
                <ExternalLink className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Abrir</span>
              </a>
              <button
                data-testid="modal-close-btn"
                onClick={() => setOpen(false)}
                className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div
            className="flex-1 max-w-6xl mx-auto w-full bg-white rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              data-testid="statute-iframe"
              src={STATUTE_URL}
              title="Estatuto Social - Fundación Futuros del Deporte"
              className="w-full h-full min-h-[70vh]"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Statute;
